// src/features/team/RoleFormDialog.tsx
// Create or edit a team role — form-kit dialog (docs/ux/14-forms.md) used by
// both TeamRolesPage (create) and TeamRoleDetailPage (edit).

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useTeam } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { IconPicker, ColorPicker, PermissionsEditor } from './RolePickers';
import type { TeamPermission } from '@core/types/team/TeamPermission';

export interface RoleFormValue {
  id?: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  priority: number;
  is_default: boolean;
}

const EMPTY: RoleFormValue = {
  name: '',
  description: '',
  icon: '',
  color: '#6366f1',
  priority: 0,
  is_default: false,
};

interface RoleFormDialogProps {
  open: boolean;
  teamId: string;
  /** Present when editing an existing role; absent when creating. */
  role?: RoleFormValue | null;
  initialPermissions?: TeamPermission[];
  onClose: () => void;
  onSaved: () => void;
}

export function RoleFormDialog({ open, teamId, role, initialPermissions, onClose, onSaved }: RoleFormDialogProps) {
  const { t } = useI18n();
  const store = useTeam();
  const isEdit = !!role;

  const [form, setForm] = useState<RoleFormValue>(EMPTY);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [moreFields, setMoreFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(role ?? EMPTY);
    setPermissions(new Set((initialPermissions ?? []).map((p) => p.id)));
    setMoreFields(false);
    setError(null);
  }, [open, role, initialPermissions]);

  const dirty = isEdit
    ? form.name !== role?.name || form.description !== role?.description || form.icon !== role?.icon ||
      form.color !== role?.color || form.priority !== role?.priority
    : !!(form.name.trim() || form.description.trim());

  const submit = async () => {
    if (!form.name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && role?.id) {
        await store.UpdateTeamRole(teamId, role.id, {
          name: form.name.trim(),
          description: form.description.trim(),
          icon: form.icon,
          color: form.color,
          priority: form.priority,
          is_default: form.is_default,
        } as never);
      } else {
        const res = await store.CreateTeamRole(teamId, {
          role: {
            name: form.name.trim(),
            description: form.description.trim(),
            icon: form.icon,
            color: form.color,
            priority: form.priority,
            is_default: form.is_default,
            is_system: false,
          } as never,
        });
        const newRoleId = (res as any)?.role?.entity?.id || (res as any)?.role?.id;
        if (newRoleId && permissions.size > 0) {
          const defs = permissions.size > 0
            ? Array.from(permissions).map((id) => ({ id })) as TeamPermission[]
            : [];
          try {
            await store.AssignTeamPermissionToRole(teamId, newRoleId, { permissions: { permissions: defs } });
          } catch { /* role still created */ }
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('team.createRole')}
      canSubmit={!!form.name.trim()}
      isSubmitting={submitting}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('team.editRole') : t('team.createRole')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('common.fewerFields') : t('common.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={form.name}
          onChange={(name) => setForm((p) => ({ ...p, name }))}
          placeholder={t('team.roleField.namePlaceholder')}
          initialFocus
          aria-label={t('team.roleField.name')}
        />
        <AutoGrowTextarea
          value={form.description}
          onChange={(description) => setForm((p) => ({ ...p, description }))}
          placeholder={t('team.roleField.descriptionPlaceholder')}
          minRows={1}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 px-3.5 pb-3 sm:grid-cols-2">
        <IconPicker value={form.icon} onChange={(icon) => setForm((p) => ({ ...p, icon }))} color={form.color} />
        <ColorPicker value={form.color} onChange={(color) => setForm((p) => ({ ...p, color }))} />
      </div>

      {moreFields && (
        <div className="border-t border-border px-3.5 py-3 space-y-3">
          <FormRow label={t('team.roleField.priority')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={form.priority === 0 ? '' : String(form.priority)}
              onChange={(e) => {
                const v = e.target.value;
                if (v !== '' && !/^\d+$/.test(v)) return;
                setForm((p) => ({ ...p, priority: v === '' ? 0 : parseInt(v, 10) }));
              }}
              placeholder="0"
              className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('team.roleField.isDefault')} labelWidth="sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.checked }))}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
          </FormRow>
        </div>
      )}

      {!isEdit && (
        <div className="border-t border-border px-3.5 py-3">
          <PermissionsEditor value={permissions} onChange={setPermissions} t={t} />
        </div>
      )}
    </FormDialog>
  );
}

export default RoleFormDialog;
